import { useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { WELL } from "@/base/well";
import { TOUCH_TARGET } from "@/components/buttonStyles";

// A box on a record tab that collapses (BRK-S399; the submission wireframe:
// "Every box collapses"). The title is a heading whose button toggles the
// box — the disclosure pattern, so a screen reader hears "expanded" or
// "collapsed" and the heading outline still lists every box.
//
// Collapsed content is HIDDEN, not unmounted: what a box holds (a GIA being
// edited, a download in flight) survives closing and reopening it. Open by
// default, and the state is this view's only (NN #9): a reload opens it again.
//
// The box IS the well (BRK-S502 review, Aaron 2026-10-29: "every box is a
// well"): SectionCard's own WELL, with the heading and its actions inside it.
// Collapsed, the well stays with just its header row. A panel puts its content
// straight in; it never draws a second card of its own inside.

export function CollapsibleBox({
  title,
  actions,
  children,
}: {
  title: string;
  /** What the box's header carries at its right (a button, a note) — the
   *  submission wireframe's "Upload document", "Edit Bond Request". Shown
   *  whether the box is open or not; it wraps under the title at 375. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const bodyId = useId();
  const [open, setOpen] = useState(true);
  const heading = (
    <h2 className="text-heading text-brk-text">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded text-left hover:text-brk-accent ${TOUCH_TARGET}`}
      >
        <ChevronDown
          size={18}
          aria-hidden
          className={`shrink-0 text-brk-dim transition-transform ${
            open ? "" : "-rotate-90"
          }`}
        />
        {title}
      </button>
    </h2>
  );
  return (
    <div data-well className={`flex min-w-0 flex-col gap-3 ${WELL}`}>
      {actions === undefined ? (
        heading
      ) : (
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {heading}
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        </div>
      )}
      {/* `hidden` alone loses to `flex`: Tailwind's utilities come after
          preflight's `[hidden] { display: none }` at the same specificity, so
          the display class itself has to follow the state. */}
      <div
        id={bodyId}
        hidden={!open}
        className={`${open ? "flex" : "hidden"} min-w-0 flex-col gap-4`}
      >
        {children}
      </div>
    </div>
  );
}
